/**
 * Detail Card Component.
 *
 * A reusable card for displaying and inline-editing key-value property
 * lists. Used across detail pages (assets, work orders, customers,
 * vendors, POs) for a consistent experience.
 *
 * Features:
 * - Compact horizontal label-value layout (no wasted space)
 * - Double-click any value to edit inline
 * - Supports text, number, select, and textarea field types
 * - Per-field save with loading feedback
 * - Keyboard: Enter to save, Escape to cancel
 * - Optional read-only fields and custom value rendering
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { Check, X, Loader2 } from "lucide-react";
import { clsx } from "clsx";

// ---------------------------------------------------------------------------
// Field Definition
// ---------------------------------------------------------------------------

export interface DetailField {
  /** Unique key for this field (used as React key). */
  key: string;
  /** Display label. */
  label: string;
  /** Current value. */
  value: string | number | null | undefined;
  /** Input type when editing. */
  type?: "text" | "number" | "select" | "textarea";
  /** Options for select type. */
  options?: Array<{ value: string; label: string }>;
  /** Placeholder text in edit mode. */
  placeholder?: string;
  /** Whether this field is editable (default true). */
  editable?: boolean;
  /** Custom display renderer (overrides default text display). */
  render?: (value: string | number | null | undefined) => React.ReactNode;
  /** Called with the new value when the user saves. */
  onSave?: (value: string | number | null) => Promise<void>;
  /** Validation function. Return error string or null. */
  validate?: (value: string) => string | null;
  /** Format the display value (e.g., currency). */
  format?: (value: string | number | null | undefined) => string;
  /** Suffix after the value (e.g., units). */
  suffix?: string;
}

// ---------------------------------------------------------------------------
// Detail Card Props
// ---------------------------------------------------------------------------

interface DetailCardProps {
  /** Card title. */
  title: string;
  /** Fields to display. */
  fields: DetailField[];
  /** Optional header action (e.g., a button). */
  headerAction?: React.ReactNode;
  /** Additional className on the outer card. */
  className?: string;
}

// ---------------------------------------------------------------------------
// Inline Field (internal)
// ---------------------------------------------------------------------------

const inputClass =
  "w-full h-7 rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-[var(--color-glass)] px-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)] focus-visible:border-[var(--border-focus)]";

function InlineField({ field }: { field: DetailField }) {
  const editable = field.editable !== false && !!field.onSave;
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >(null);

  const displayValue = field.format
    ? field.format(field.value)
    : field.value != null && field.value !== ""
      ? String(field.value)
      : "—";

  const startEdit = useCallback(() => {
    if (!editable) return;
    setEditValue(field.value != null ? String(field.value) : "");
    setError(null);
    setIsEditing(true);
  }, [editable, field.value]);

  const cancelEdit = () => {
    setIsEditing(false);
    setError(null);
  };

  const handleSave = async () => {
    if (field.validate) {
      const err = field.validate(editValue);
      if (err) {
        setError(err);
        return;
      }
    }
    const currentStr = field.value != null ? String(field.value) : "";
    if (editValue === currentStr) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const saveValue =
        field.type === "number"
          ? editValue
            ? Number(editValue)
            : null
          : editValue || null;
      await field.onSave!(saveValue);
      setIsEditing(false);
    } catch {
      setError("Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && field.type !== "textarea") {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") cancelEdit();
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current instanceof HTMLInputElement) {
        inputRef.current.select();
      }
    }
  }, [isEditing]);

  // Edit mode.
  if (isEditing) {
    return (
      <div className="flex items-start gap-3 py-2 px-3">
        <span className="text-xs text-[var(--text-tertiary)] w-28 shrink-0 pt-1.5">
          {field.label}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            {field.type === "select" && field.options ? (
              <select
                ref={inputRef as React.RefObject<HTMLSelectElement>}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className={inputClass}
              >
                <option value="">{"—"}</option>
                {field.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : field.type === "textarea" ? (
              <textarea
                ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={field.placeholder}
                rows={2}
                className={`${inputClass} h-auto py-1`}
              />
            ) : (
              <input
                ref={inputRef as React.RefObject<HTMLInputElement>}
                type={field.type || "text"}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={field.placeholder}
                className={inputClass}
              />
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="shrink-0 h-7 w-7 flex items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-success)] hover:bg-[var(--fill-success)] disabled:opacity-50"
            >
              {isSaving ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
            </button>
            <button
              type="button"
              onClick={cancelEdit}
              disabled={isSaving}
              className="shrink-0 h-7 w-7 flex items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-danger)] hover:bg-[var(--fill-danger)]"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          {error && (
            <p className="text-[10px] text-[var(--text-danger)] mt-0.5">
              {error}
            </p>
          )}
        </div>
      </div>
    );
  }

  // View mode.
  return (
    <div
      className={clsx(
        "flex gap-3 py-2 px-3 rounded-[var(--radius-sm)] transition-colors",
        field.render ? "items-center" : "items-baseline",
        editable && "cursor-pointer hover:bg-[var(--surface-sunken)]",
      )}
      onDoubleClick={startEdit}
      title={editable ? "Double-click to edit" : undefined}
    >
      <span className="text-xs text-[var(--text-tertiary)] w-28 shrink-0">
        {field.label}
      </span>
      {field.render ? (
        <div className="text-sm text-[var(--text-primary)] flex-1 min-w-0">
          {field.render(field.value)}
        </div>
      ) : (
        <span className="text-sm text-[var(--text-primary)] flex-1 min-w-0 truncate">
          {displayValue}
          {field.suffix && field.value != null && field.value !== "" && (
            <span className="text-[var(--text-tertiary)] ml-1">
              {field.suffix}
            </span>
          )}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail Card
// ---------------------------------------------------------------------------

function DetailCard({
  title,
  fields,
  headerAction,
  className,
}: DetailCardProps) {
  return (
    <div
      className={clsx(
        "rounded-[var(--radius-lg)] elevation-1 animate-fade-in",
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[var(--border-subtle)] bg-[var(--surface-sunken)]/50">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
          {title}
        </h3>
        {headerAction}
      </div>

      {/* Fields */}
      <div className="divide-y divide-[var(--border-subtle)]/50">
        {fields.map((field) => (
          <InlineField key={field.key} field={field} />
        ))}
      </div>
    </div>
  );
}

export default DetailCard;
export type { DetailCardProps };
